// src/adminCalendar/components/BookingEventCard.jsx
// LOOP R0.3.1 — Unified Booking Event Card
// Single visual card used by FullCalendar, resource-day and legacy event renderers.

import React from "react";

function clean(value, fallback = "") {
  const out = String(value ?? "").trim();
  return out || fallback;
}

function number(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function dateFrom(value) {
  try {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value.toDate === "function") return value.toDate();
    if (value.seconds) return new Date(Number(value.seconds) * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}


function normalizeRoleContext(roleContext = null) {
  const ctx = roleContext && typeof roleContext === "object" ? roleContext : {};
  const rawRole = clean(ctx.role || (ctx.isWorkerView ? "worker" : ctx.isOwnerOrAdmin ? "owner" : "staff"), "staff").toLowerCase();
  const role = ["owner", "admin", "worker", "staff"].includes(rawRole) ? rawRole : "staff";
  const isWorker = role === "worker" || ctx.isWorkerView === true;
  const isOwnerOrAdmin = role === "owner" || role === "admin" || ctx.isOwnerOrAdmin === true;
  return {
    role,
    isWorker,
    isOwnerOrAdmin,
    label: clean(ctx.label || (isOwnerOrAdmin ? "Owner/Admin" : isWorker ? "Trabajador" : "Equipo")),
    sublabel: clean(ctx.sublabel || (isOwnerOrAdmin ? "Vista completa" : isWorker ? "Mi agenda" : "Vista operativa")),
    canViewPrices: ctx.canViewPrices === true || (isOwnerOrAdmin && ctx.canViewPrices !== false),
    canViewAllCalendars: ctx.canViewAllCalendars === true || isOwnerOrAdmin,
  };
}

function rawFromArg(arg = {}) {
  const event = arg?.event || arg || {};
  const extended = event?.extendedProps || {};
  return extended.raw || extended.booking || arg?.raw || arg?.booking || event?.raw || event?.booking || {};
}

export function bookingEventStatusInfo(value) {
  const s = clean(value).toLowerCase();
  if (["confirmed", "confirmada", "approved", "aprobada", "accepted"].includes(s)) return { label: "Confirmada", tone: "confirmed" };
  if (["pending", "pendiente", "to_confirm", "por_confirmar", "requested"].includes(s)) return { label: "Pendiente", tone: "pending" };
  if (["started", "in-progress", "in_progress", "en_progreso"].includes(s)) return { label: "En progreso", tone: "progress" };
  if (["completed", "complete", "done", "completada"].includes(s)) return { label: "Completada", tone: "completed" };
  if (["no-show", "noshow", "no_show", "no asistió", "no_asistio"].includes(s)) return { label: "No asistió", tone: "warning" };
  if (["cancelled", "canceled", "cancelada"].includes(s)) return { label: "Cancelada", tone: "cancelled" };
  if (["rejected", "declined", "rechazada"].includes(s)) return { label: "Rechazada", tone: "cancelled" };
  return { label: s ? s.charAt(0).toUpperCase() + s.slice(1) : "Reserva", tone: "neutral" };
}

function bookingClientName(raw = {}, fallback = "Cliente") {
  return clean(raw.customerName || raw.clientName || raw.name || raw.customer?.name || raw.customerSnapshot?.name, fallback);
}

function bookingServiceName(raw = {}, fallback = "Reserva") {
  return clean(raw.serviceName || raw.service || raw.packageName || raw.serviceTitle || raw.title, fallback) + (raw.sessionProgramId ? ` · Sesión ${raw.sessionNumber} de ${raw.sessionTotal}` : '');
}

function bookingWorkerName(raw = {}) {
  return clean(raw.employeeName || raw.workerName || raw.assignedEmployeeName || raw.resourceName || raw.employee?.name || raw.worker?.name || "");
}

function bookingPriceLabel(raw = {}) {
  const value = raw.totalPrice ?? raw.total ?? raw.price ?? raw.servicePrice ?? raw.amount ?? raw.paidAmount ?? null;
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

function bookingMembershipLabel(raw = {}) {
  const applied =
    raw.membershipApplied === true ||
    raw.membershipBenefitApplied === true ||
    Boolean(raw.customerMembershipId || raw.membershipId || raw.membershipPlanName || raw.membershipBenefitPlanName);
  if (!applied) return "";

  const planName = clean(raw.membershipPlanName || raw.membershipBenefitPlanName || raw.membershipName || "");
  const credits = number(raw.membershipCreditsUsed ?? raw.creditsUsed, 0);
  if (credits > 0) return planName ? `Membresía · ${planName}` : "Membresía";
  return planName ? `Membresía · ${planName}` : "Membresía";
}

function bookingLoyaltyLabel(raw = {}) {
  const paymentMethod = clean(raw.paymentMethod || raw.payment?.method || "").toLowerCase();
  const coverageType = clean(raw.paymentCoverageType || raw.coverageType || raw.payment?.paymentCoverageType || raw.payment?.coverageType || "").toLowerCase();
  const paymentStatus = clean(raw.paymentStatus || raw.payment?.status || "").toLowerCase();

  const reversed =
    raw.loyaltyRewardReversed === true ||
    raw.loyaltyRedemptionReversed === true ||
    raw.payment?.loyaltyRewardReversed === true ||
    raw.payment?.loyaltyRedemptionReversed === true;

  if (reversed) return "";

  const loyaltyMethods = new Set(["loyalty_reward", "loyalty", "fidelidad", "reward", "recompensa"]);
  const cashLikeMethods = new Set(["cash", "efectivo", "yappy", "japi", "yappi", "card", "tarjeta", "credit_card", "debit_card", "transfer", "transferencia", "bank_transfer", "other", "otro"]);

  // Current payment state wins over historical loyalty fields.
  // Reversal keeps loyaltyRedemptionEventId/token/programId as audit trail, but those
  // fields alone should not keep the agenda badge alive.
  if (paymentMethod && cashLikeMethods.has(paymentMethod)) return "";
  if (paymentMethod && !loyaltyMethods.has(paymentMethod) && paymentStatus !== "covered") return "";

  const appliedByPayment = loyaltyMethods.has(paymentMethod) || loyaltyMethods.has(coverageType);
  const appliedByFlag =
    raw.loyaltyRewardApplied === true ||
    raw.payment?.loyaltyRewardApplied === true;

  if (!appliedByPayment && !appliedByFlag) return "";

  const reward = clean(raw.loyaltyRewardLabel || raw.payment?.loyaltyRewardLabel || raw.rewardLabel || "");
  return reward ? `Fidelidad · ${reward}` : "Fidelidad";
}

function bookingDurationMinutes(raw = {}, start = null, end = null) {
  const explicit = number(raw.duration ?? raw.durationMinutes ?? raw.durationMins ?? raw.minutes ?? raw.visitMinutes, 0);
  if (explicit > 0) return explicit;
  if (start && end) {
    const diff = Math.round((end.getTime() - start.getTime()) / 60000);
    if (Number.isFinite(diff) && diff > 0) return diff;
  }
  return null;
}

function bookingDurationLabel(raw = {}, start = null, end = null) {
  const mins = bookingDurationMinutes(raw, start, end);
  if (!mins) return "";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

function bookingLocationSummary(raw = {}) {
  const loc = raw.customerLocation || raw.location || {};
  const matched = raw.serviceAreaMatched || raw.matchedServiceArea || {};
  const compact = clean(
    raw.locationLabel ||
    raw.customerLocationLabel ||
    raw.serviceAreaLabel ||
    matched.label ||
    loc.label ||
    raw.coverageArea ||
    raw.address ||
    ""
  );
  const mode = clean(raw.visitMode || raw.visitTypeLocation || raw.serviceMode).toLowerCase();
  const isDelivery = mode === "delivery" || mode === "domicilio" || Boolean(raw.customerLocation || raw.serviceAreaMatched || raw.deliveryAddress || raw.customerAddress);
  return { compact, isDelivery };
}

function toneIndex(value) {
  const text = clean(value, "Reserva").toLowerCase();
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 997;
  return hash % 6;
}

function toneClass(raw = {}, serviceName = "Reserva", colorMode = "service", statusInfo = null) {
  const safeMode = ["service", "worker", "status"].includes(colorMode) ? colorMode : "service";
  if (safeMode === "status") return `status-tone-${statusInfo?.tone || "neutral"}`;
  if (safeMode === "worker") return `worker-tone-${toneIndex(bookingWorkerName(raw) || raw.employeeId || raw.workerId || "Sin trabajador")}`;
  return `service-tone-${toneIndex(serviceName)}`;
}

function accentFor(raw = {}, serviceName = "Reserva", colorMode = "service", statusInfo = null) {
  if (raw.accent || raw.color) return raw.accent || raw.color;
  const servicePalette = ["#0f766e", "#7c3aed", "#2563eb", "#db2777", "#ea580c", "#0891b2"];
  const workerPalette = ["#047857", "#6d28d9", "#1d4ed8", "#be185d", "#c2410c", "#0e7490"];
  const statusPalette = { confirmed: "#0f766e", pending: "#d97706", progress: "#2563eb", completed: "#15803d", warning: "#ca8a04", cancelled: "#dc2626", neutral: "#64748b" };
  const safeMode = ["service", "worker", "status"].includes(colorMode) ? colorMode : "service";
  if (safeMode === "status") return statusPalette[statusInfo?.tone || "neutral"] || statusPalette.neutral;
  if (safeMode === "worker") return workerPalette[toneIndex(bookingWorkerName(raw) || raw.employeeId || raw.workerId || "worker")];
  return servicePalette[toneIndex(serviceName)];
}

function formatTime(date, timeFormat = "24h") {
  const d = dateFrom(date);
  if (!d) return "";
  const hour12 = String(timeFormat || "24h") === "12h";
  return d.toLocaleTimeString(hour12 ? "en-US" : "es-PA", { hour: "numeric", minute: "2-digit", hour12 });
}

function formatRange(start, end, timeFormat = "24h", fallback = "") {
  const left = formatTime(start, timeFormat);
  const right = formatTime(end, timeFormat);
  if (left && right) return `${left} – ${right}`;
  return left || fallback || "";
}

function resolveCardData({ arg, raw: rawProp, event: eventProp, timeFormat, colorMode, pixelHeight }) {
  const event = eventProp || arg?.event || arg || {};
  const raw = rawProp || rawFromArg(arg || event);
  const start = dateFrom(event.start || raw.start || raw.startAt || raw.scheduledAt || raw.dateTime);
  const end = dateFrom(event.end || raw.end || raw.endAt || raw.endsAt);
  const status = bookingEventStatusInfo(raw.status || raw.confirmationStatus || event?.extendedProps?.status);
  const serviceName = bookingServiceName(raw, event.title || "Reserva");
  const clientName = bookingClientName(raw);
  const workerName = bookingWorkerName(raw);
  const priceLabel = bookingPriceLabel(raw);
  const membershipLabel = bookingMembershipLabel(raw);
  const loyaltyLabel = bookingLoyaltyLabel(raw);
  const durationLabel = bookingDurationLabel(raw, start, end);
  const location = bookingLocationSummary(raw);
  const durationMinutes = bookingDurationMinutes(raw, start, end);
  const h = number(pixelHeight ?? event?.extendedProps?.loopEventPixelHeight, 0);
  const density = h > 0 ? (h <= 30 ? "micro" : h <= 42 ? "tiny" : h <= 62 ? "short" : "standard") : (durationMinutes ? (durationMinutes <= 20 ? "micro" : durationMinutes <= 30 ? "tiny" : durationMinutes <= 45 ? "short" : "standard") : "standard");
  const safeColorMode = ["service", "worker", "status"].includes(colorMode) ? colorMode : "service";
  const accent = accentFor(raw, serviceName, safeColorMode, status);
  const displayTime = formatRange(start, end, timeFormat, arg?.timeText || "");
  const titleText = [displayTime, status.label, clientName, serviceName, workerName, location.compact, priceLabel, membershipLabel, loyaltyLabel].filter(Boolean).join(" · ");
  return { raw, start, end, status, serviceName, clientName, workerName, priceLabel, membershipLabel, loyaltyLabel, durationLabel, location, density, safeColorMode, accent, displayTime, titleText };
}

// LOOP CALENDAR BOOKING CARD UNIFICATION V1 — one information hierarchy across web/mobile/native.
// Priority: client -> service -> time -> price/status. Density changes only by available event height/overlap.
export function bookingListSummary(event, roleContext, timeFormat) {
  const data = resolveCardData({ event, timeFormat, pixelHeight:140 });
  const role = normalizeRoleContext(roleContext);
  return { ...data, priceLabel:role.canViewPrices ? data.priceLabel : '', workerName:role.isWorker && !role.canViewAllCalendars ? '' : data.workerName };
}

export default function BookingEventCard({
  arg,
  raw,
  event,
  timeFormat = "24h",
  colorMode = "service",
  pixelHeight = null,
  variant = "calendar",
  className = "",
  style = {},
  onClick,
  type = "button",
  roleContext = null,
  showWorker = variant !== "calendar",
}) {
  const data = resolveCardData({ arg, raw, event, timeFormat, colorMode, pixelHeight });
  const densityClass = `is-${data.density}`;
  const tone = toneClass(data.raw, data.serviceName, data.safeColorMode, data.status);
  const role = normalizeRoleContext(roleContext);
  const roleClass = `role-${role.role}${role.isWorker ? " is-worker-card" : ""}${role.isOwnerOrAdmin ? " is-owner-card" : ""}`;
  const displayWorkerName = !showWorker || (role.isWorker && !role.canViewAllCalendars) ? "" : data.workerName;
  const displayPriceLabel = role.canViewPrices ? data.priceLabel : "";
  const eventLike = event || arg?.event || arg || {};
  const overlapColumns = Math.max(1, number(eventLike?.extendedProps?.loopOverlapColumns, 1));
  const isOverlapCard = overlapColumns > 1;
  const compactStartTime = formatTime(data.start, timeFormat);
  const visibleTime = isOverlapCard ? (compactStartTime || data.displayTime) : data.displayTime;
  const variantClass = variant && variant !== "calendar" ? `variant-${variant}` : "";
  const displayTitleText = [
    data.clientName,
    data.serviceName,
    data.displayTime,
    data.status.label,
    displayWorkerName,
    data.location.compact,
    displayPriceLabel,
    data.membershipLabel,
    data.loyaltyLabel,
  ].filter(Boolean).join(" · ");
  const CardTag = onClick ? "button" : "div";
  const cardClass = `loop-booking-event-card loop-cal-event-card ${tone} ${roleClass} status-${data.status.tone} tone-by-${data.safeColorMode} ${densityClass} ${isOverlapCard ? "is-overlap-card" : ""} ${variantClass} ${className}`.trim();
  const cardProps = {
    className: cardClass,
    style: { "--event-accent": data.accent, ...style },
    title: displayTitleText,
    onClick,
    type: onClick ? type : undefined,
  };

  if (data.density === "micro") {
    return (
      <CardTag {...cardProps}>
        <div className="loop-cal-event-micro-row">
          <strong className="loop-cal-event-client">{data.clientName}</strong>
          <span className="loop-cal-event-time">{compactStartTime || visibleTime}</span>
        </div>
      </CardTag>
    );
  }

  if (data.density === "tiny") {
    return (
      <CardTag {...cardProps}>
        <div className="loop-cal-event-head">
          <strong className="loop-cal-event-client">{data.clientName}</strong>
          {!isOverlapCard && displayPriceLabel ? <b className="loop-cal-event-price">{displayPriceLabel}</b> : null}
        </div>
        <div className="loop-cal-event-compact-bottom">
          <span className="loop-cal-event-service">{data.serviceName}</span>
          <span className="loop-cal-event-time">{compactStartTime || visibleTime}</span>
        </div>
      </CardTag>
    );
  }

  if (data.density === "short") {
    return (
      <CardTag {...cardProps}>
        <div className="loop-cal-event-head">
          <strong className="loop-cal-event-client">{data.clientName}</strong>
          {!isOverlapCard && displayPriceLabel ? <b className="loop-cal-event-price">{displayPriceLabel}</b> : null}
        </div>
        <div className="loop-cal-event-service">{data.serviceName}</div>
        <div className="loop-cal-event-meta-row">
          <span className="loop-cal-event-time">{visibleTime}</span>
          <i className={`loop-cal-event-status-dot ${data.status.tone}`} title={data.status.label} aria-label={data.status.label} />
        </div>
      </CardTag>
    );
  }

  return (
    <CardTag {...cardProps}>
      <div className="loop-cal-event-head">
        <strong className="loop-cal-event-client">{data.clientName}</strong>
        {!isOverlapCard && displayPriceLabel ? <b className="loop-cal-event-price">{displayPriceLabel}</b> : null}
      </div>
      <div className="loop-cal-event-service">{data.serviceName}</div>
      <div className="loop-cal-event-meta-row">
        <span className="loop-cal-event-time">{visibleTime}</span>
        <span className={`loop-cal-event-status-pill ${data.status.tone}`}>{data.status.label}</span>
      </div>
      <div className="loop-cal-event-footer">
        {displayWorkerName ? <span className="loop-cal-event-chip worker">{displayWorkerName}</span> : null}
        {data.durationLabel ? <span className="loop-cal-event-chip duration">{data.durationLabel}</span> : null}
        {data.location.compact ? <span className={`loop-cal-event-chip location ${data.location.isDelivery ? "delivery" : "onsite"}`}>{data.location.compact}</span> : null}
        {data.membershipLabel ? <span className="loop-cal-event-chip membership">Membresía</span> : null}
        {data.loyaltyLabel ? <span className="loop-cal-event-chip loyalty">Fidelidad</span> : null}
      </div>
    </CardTag>
  );
}
